'use client';

import { Avatar } from '@/components/ds';
import { resolveDisplayName } from '@/lib/displayName';

interface UserAvatarProps {
  user: {
    avatar_url?: string;
    avatar?: string;
    display_name?: string;
    full_name?: string;
    name?: string;
    username?: string;
  };
  size?: 'sm' | 'md' | 'lg';
  className?: string;
  online?: boolean;
}

// Legacy shim — delegates to the canonical ds Avatar so the fallback
// (initials, deterministic color, online dot, image onError) matches every
// other surface. Existing call sites keep their { user, size: 'sm'|'md'|'lg' }
// shape; new code should import Avatar from @/components/ds directly.
export default function UserAvatar({ user, size = 'md', className, online }: UserAvatarProps) {
  const rawAvatar = (user.avatar_url ?? user.avatar ?? '').trim();
  const isPlaceholder =
    rawAvatar === '/placeholder-avatar.png' ||
    rawAvatar.endsWith('/placeholder-avatar.png');
  const src = rawAvatar && !isPlaceholder ? rawAvatar : undefined;
  // Pre-resolved name passed from upstream (post.user.name etc.) wins —
  // those callers have already run resolveDisplayName. Otherwise fall back
  // through the canonical chain so initials line up with what the rest of
  // the app would render.
  const name =
    user.name?.trim() || resolveDisplayName(user, 'User');

  // Wire username through so feed post / comment avatars become a portal to
  // the author's profile. Orphaned users (no username) stay non-clickable.
  return (
    <Avatar
      src={src}
      name={name}
      size={size}
      online={online}
      className={className}
      username={user.username || null}
    />
  );
}
